/**
 * 动态菜单配置服务
 * 负责合并本地静态配置和接口权限数据
 * @author 系统
 * @date 2024
 */

import { allNavigations } from '@/config/navigation'
import type { NavigationConfig, ApiMenuConfig, NavigationItem, ModuleNavigation, UserPermission } from '../types/navigation'

/**
 * 动态菜单配置服务类
 */
class MenuConfigService {
  /**
   * 获取导航配置
   * @param userPermissions 用户权限数据，支持完整对象数组或权限代码字符串数组
   * @returns 导航配置数组
   */
  static async getNavigationConfig(userPermissions?: UserPermission[] | string[]): Promise<NavigationConfig[]> {
    try {
      if (!userPermissions || userPermissions.length === 0) {
        return this.getDefaultNavigationConfig()
      }

      // 如果是字符串数组（如 ['aimanagement.view', 'tenant.view']），基于本地配置过滤
      if (typeof userPermissions[0] === 'string') {
        return this.filterNavigationByPermissionCodes(userPermissions as string[])
      }

      // 完整对象数组，直接转换
      return this.convertApiMenuToNavigationConfig(userPermissions as ApiMenuConfig[])
    } catch (error) {
      console.error('获取导航配置失败:', error)
      return this.getDefaultNavigationConfig()
    }
  }

  /**
   * 基于权限代码字符串数组过滤本地导航配置
   * 兼容后端返回的 '.' 分隔格式（自动转换为 ':' 格式匹配）
   */
  static filterNavigationByPermissionCodes(permissionCodes: string[]): NavigationConfig[] {
    if (!permissionCodes || permissionCodes.length === 0) {
      return []
    }

    // 标准化权限代码：把 '.' 换成 ':'，并去重
    const normalizedCodes = new Set(
      permissionCodes.map(code => code.replace(/\./g, ':'))
    )
    const hasWildcard = normalizedCodes.has('*')

    const result: NavigationConfig[] = []

    for (const nav of allNavigations) {
      // 模块级权限（navigation.permissions）
      const modulePermissions = nav.permissions || []
      const hasModulePermission = hasWildcard ||
        modulePermissions.length === 0 ||
        modulePermissions.some((p: string) => normalizedCodes.has(p.replace(/\./g, ':')))

      // 过滤出有权限的菜单项
      let filteredItems = nav.items.filter(item => {
        const itemPermissions = item.permissions || []
        // 无权限要求的菜单默认显示
        if (itemPermissions.length === 0) return true
        // 通配符权限直接放行
        if (hasWildcard) return true
        // 任一权限匹配即可
        return itemPermissions.some(p => {
          const normalizedP = p.replace(/\./g, ':')
          return normalizedCodes.has(normalizedP)
        })
      })

      // 过滤掉 meta.hideInMenu = true 的项
      filteredItems = filteredItems.filter(item => !item.meta?.hideInMenu)

      // 如果过滤后没有子项，但用户有模块级权限，自动生成模块入口
      if (filteredItems.length === 0 && hasModulePermission) {
        // 尝试找到第一个非 hideInMenu 且有 path 的项作为入口路径
        const entryItem = nav.items.find(item => item.path && !item.meta?.hideInMenu)
        if (entryItem) {
          filteredItems.push({
            ...entryItem,
            permissions: modulePermissions.filter(Boolean),
            meta: { ...entryItem.meta, hideInMenu: false }
          })
        } else if (nav.items.length > 0) {
          // 回退：使用第一个子项的路径生成入口
          const firstItem = nav.items[0]
          filteredItems.push({
            ...firstItem,
            id: nav.name,
            title: nav.title,
            path: firstItem.path || `/${nav.name}`,
            icon: nav.icon,
            permissions: modulePermissions.length > 0 ? modulePermissions : [''],
            meta: { ...firstItem.meta, hideInMenu: false }
          })
        }
      }

      if (filteredItems.length === 0) continue

      // 收集该导航配置涉及的所有权限
      const allPermissions = [
        ...modulePermissions,
        ...filteredItems.flatMap(item => item.permissions || [])
      ].filter(Boolean)

      result.push({
        name: nav.name,
        title: nav.title,
        icon: nav.icon,
        order: nav.order,
        items: filteredItems,
        permissions: Array.from(new Set(allPermissions))
      })
    }

    return result.sort((a, b) => (a.order || 999) - (b.order || 999))
  }

  /**
   * 获取默认导航配置
   * 仅在本地开发或无权限数据时使用，返回空数组避免安全风险
   */
  static getDefaultNavigationConfig(): NavigationConfig[] {
    // 生产环境下不允许无权限回退到默认导航
    if (import.meta.env.PROD) {
      console.warn('[MenuConfigService] 生产环境不允许无权限回退，返回空导航')
      return []
    }
    // 开发环境下返回默认导航方便调试
    return allNavigations.map(nav => ({
      name: nav.name,
      title: nav.title,
      icon: nav.icon,
      order: nav.order,
      permissions: [],
      items: nav.items.map(item => ({
        ...item,
        permissions: []
      }))
    }))
  }

  /**
   * 将API菜单配置转换为导航配置
   * @param apiMenuConfig API返回的菜单配置
   * @returns 导航配置数组
   */
  static convertApiMenuToNavigationConfig(apiMenuConfig: ApiMenuConfig[]): NavigationConfig[] {
    if (!apiMenuConfig || apiMenuConfig.length === 0) {
      return []
    }

    try {
      // 过滤出有效的菜单项（包括 module 一级菜单和 menu 二级菜单）
      const activeMenus = apiMenuConfig.filter(item =>
        (item.type === 'module' || item.type === 'menu') &&
        (item.status === 'active' || (item.isShow !== false && !item.deletedAt))
      )

      // 分离 module 和 menu
      const modules = activeMenus.filter(item => item.type === 'module')
      const menus = activeMenus.filter(item => item.type === 'menu')

      const navigationConfigs: NavigationConfig[] = []

      // 动态创建首页配置（从 API 数据中获取）
      const dashboardConfig = this.createDashboardConfigFromApi(apiMenuConfig)
      if (dashboardConfig) {
        navigationConfigs.push(dashboardConfig)
      }

      // 为每个 module 创建 NavigationConfig
      for (const module of modules) {
        const moduleCode = module.code ?? module.module ?? module.name ?? String(module.id)
        // 找到属于这个模块的所有菜单项
        const moduleMenus = menus.filter(menu => menu.parentId === module.id)

        // 构建菜单项
        const navigationItems: NavigationItem[] = moduleMenus
          .map(menu => {
            const code = menu.code ?? String(menu.id)
            return {
              id: code,
              title: menu.name,
              path: menu.path || menu.router || '',
              icon: menu.icon,
              order: menu.orderNum || 0,
              hidden: menu.status !== 'active',
              permissions: menu.code ? [menu.code] : []
            }
          })
          .sort((a, b) => (a.order || 0) - (b.order || 0))

        // 创建模块配置
        const config: NavigationConfig = {
          name: module.module || module.code || String(module.id),
          title: module.name,
          icon: module.icon,
          order: module.orderNum || 0,
          items: navigationItems,
          permissions: [moduleCode, ...navigationItems.map(item => item.permissions || []).flat()].filter(Boolean)
        }

        navigationConfigs.push(config)
      }

      // 按排序号排序
      return navigationConfigs.sort((a, b) => (a.order || 0) - (b.order || 0))
    } catch (error) {
      console.error('转换API菜单配置失败:', error)
      return []
    }
  }

  /**
   * 从API数据中动态创建首页配置
   * @param apiMenuConfig API返回的菜单配置
   * @returns 首页导航配置，如果没有找到首页数据则返回null
   */
  private static createDashboardConfigFromApi(apiMenuConfig: ApiMenuConfig[]): NavigationConfig | null {
    // 查找首页配置（code为 home:view）
    const homeMenu = apiMenuConfig.find(menu => menu.code === 'home:view')

    if (!homeMenu) {
      console.warn('未找到首页配置 (code: home:view)，将不显示首页菜单')
      return null
    }

    // 从API数据动态构建首页配置
    const homeCode = homeMenu.code ?? String(homeMenu.id)
    return {
      name: 'dashboard',
      title: homeMenu.name || '首页',
      icon: homeMenu.icon || 'House',
      order: homeMenu.orderNum ?? 0,
      items: [{
        id: 'dashboard',
        title: homeMenu.name || '首页',
        path: homeMenu.router || '/home',
        icon: homeMenu.icon || 'House',
        order: homeMenu.orderNum ?? 0,
        permissions: homeMenu.code ? [homeMenu.code] : [],
        affix: true,
        hidden: homeMenu.isShow === false
      }],
      permissions: [homeCode],
      hidden: homeMenu.isShow === false
    }
  }
  private static instance: MenuConfigService
  private cachedMenuConfig: NavigationConfig[] = []
  private lastUpdateTime: number = 0

  /**
   * 获取服务实例（单例模式）
   */
  static getInstance(): MenuConfigService {
    if (!MenuConfigService.instance) {
      MenuConfigService.instance = new MenuConfigService()
    }
    return MenuConfigService.instance
  }

  /**
   * 合并本地配置和接口数据
   * @param localConfigs 本地静态配置
   * @param apiMenus 接口菜单数据
   * @param userPermissions 用户权限代码列表
   * @returns 合并后的菜单配置
   */
  async mergeConfigurations(
    localConfigs: ModuleNavigation[],
    apiMenus: ApiMenuConfig[],
    userPermissions: string[]
  ): Promise<NavigationConfig[]> {
    const mergedConfigs: NavigationConfig[] = []

    for (const localConfig of localConfigs) {
      // 转换为NavigationConfig格式
      const navigationConfig: NavigationConfig = {
        ...localConfig,
        permissions: this.extractPermissionsFromNavigation(localConfig)
      }

      // 权限检查
      if (!this.hasPermission(navigationConfig.permissions || [], userPermissions)) {
        continue
      }

      // 处理标准模块（首页配置已在 convertApiMenuToNavigationConfig 中动态生成）
      const processedConfig = this.mergeStandardModule(navigationConfig, apiMenus, userPermissions)
      if (processedConfig) {
        mergedConfigs.push(processedConfig)
      }
    }

    // 缓存结果
    this.cachedMenuConfig = mergedConfigs
    this.lastUpdateTime = Date.now()

    return mergedConfigs
  }

  /**
   * 合并标准模块配置
   * @param localConfig 本地配置
   * @param apiMenus 接口菜单数据
   * @param userPermissions 用户权限
   * @returns 合并后的配置
   */
  private mergeStandardModule(
    localConfig: NavigationConfig,
    apiMenus: ApiMenuConfig[],
    userPermissions: string[]
  ): NavigationConfig | null {
    // 过滤有权限的导航项
    const filteredItems = localConfig.items.filter(item => {
      const itemPermissions = item.permissions || []
      return itemPermissions.length === 0 || this.hasPermission(itemPermissions, userPermissions)
    })

    if (filteredItems.length === 0) {
      return null
    }

    // 合并每个导航项的配置
    const mergedItems = filteredItems.map(item => {
      // 查找对应的接口配置
      const apiMenu = apiMenus.find(menu => {
        if (!item.permissions) return false
        if (!menu.code) return false
        return item.permissions.includes(menu.code)
      })

      return {
        ...item,
        title: apiMenu?.name || item.title,
        icon: apiMenu?.icon || item.icon,
        order: apiMenu?.orderNum ?? item.order,
        hidden: apiMenu?.isShow === false || item.hidden,
        // 递归处理子菜单
        children: item.children ? this.mergeNavigationItems(item.children, apiMenus, userPermissions) : undefined
      }
    })

    return {
      ...localConfig,
      items: mergedItems.sort((a, b) => (a.order || 999) - (b.order || 999))
    }
  }

  /**
   * 合并导航项列表
   * @param items 导航项列表
   * @param apiMenus 接口菜单数据
   * @param userPermissions 用户权限
   * @returns 合并后的导航项列表
   */
  private mergeNavigationItems(
    items: NavigationItem[],
    apiMenus: ApiMenuConfig[],
    userPermissions: string[]
  ): NavigationItem[] {
    return items
      .filter(item => {
        const itemPermissions = item.permissions || []
        return itemPermissions.length === 0 || this.hasPermission(itemPermissions, userPermissions)
      })
      .map(item => {
        const apiMenu = apiMenus.find(menu => {
          if (!item.permissions) return false
          if (!menu.code) return false
          return item.permissions.includes(menu.code)
        })

        return {
          ...item,
          title: apiMenu?.name || item.title,
          icon: apiMenu?.icon || item.icon,
          order: apiMenu?.orderNum ?? item.order,
          hidden: apiMenu?.isShow === false || item.hidden,
          children: item.children ? this.mergeNavigationItems(item.children, apiMenus, userPermissions) : undefined
        }
      })
      .sort((a, b) => (a.order || 999) - (b.order || 999))
  }

  /**
   * 从导航配置中提取权限列表
   * @param navigation 导航配置
   * @returns 权限列表
   */
  private extractPermissionsFromNavigation(navigation: ModuleNavigation): string[] {
    const permissions: string[] = []

    navigation.items.forEach(item => {
      if (item.permissions) {
        permissions.push(...item.permissions)
      }
      if (item.children) {
        item.children.forEach(child => {
          if (child.permissions) {
            permissions.push(...child.permissions)
          }
        })
      }
    })

    return Array.from(new Set(permissions)) // 去重
  }

  /**
   * 检查是否有权限
   * @param requiredPermissions 所需权限
   * @param userPermissions 用户权限
   * @returns 是否有权限
   */
  private hasPermission(requiredPermissions: string[], userPermissions: string[]): boolean {
    if (requiredPermissions.length === 0) return true
    return requiredPermissions.some(permission => userPermissions.includes(permission))
  }

  /**
   * 清除缓存
   */
  clearCache(): void {
    this.cachedMenuConfig = []
    this.lastUpdateTime = 0
  }

  /**
   * 获取缓存的配置
   */
  getCachedConfig(): NavigationConfig[] {
    return this.cachedMenuConfig
  }

  /**
   * 获取最后更新时间
   */
  getLastUpdateTime(): number {
    return this.lastUpdateTime
  }

  /**
   * 检查缓存是否有效
   */
  isCacheValid(maxAge: number = 5 * 60 * 1000): boolean {
    return Date.now() - this.lastUpdateTime < maxAge
  }
}

export default MenuConfigService
